import { useMemo, useCallback } from "react";
import { useFilterContext } from "@/contexts/FilterContext";
import type { SimpleTableConfig, BaseEntity, FieldValue } from "../../types";

/**
 * Hook to calculate the number of active filters
 * Counts both simple filters and advanced filters
 */
export const useActiveFilterCount = <TData extends BaseEntity>() => {
  const filterContext = useFilterContext<TData>();
  const { filters } = filterContext;

  return useMemo(() => {
    const basicFilters = Object.keys(filters.simple).filter(
      (key) => filters.simple[key] && filters.simple[key] !== "all" && filters.simple[key] !== ""
    ).length;
    const advancedFilters = filters.advanced.length;
    return basicFilters + advancedFilters;
  }, [filters.simple, filters.advanced]);
};

/**
 * Hook to check if search is currently active
 */
export const useHasActiveSearch = <TData extends BaseEntity>() => {
  const filterContext = useFilterContext<TData>();
  const { filters } = filterContext;

  return useMemo(() => {
    return filters.search.trim().length > 0;
  }, [filters.search]);
};

/**
 * Hook to generate quick filter presets based on config
 * Uses configSavedViews if available, otherwise generates entity-specific presets
 */
export const useQuickFilterPresets = <TData extends BaseEntity>(
  config: SimpleTableConfig<TData>,
  configSavedViews?: Array<{ name: string; filters: Record<string, FieldValue> }>
) => {
  return useMemo(() => {
    // If configSavedViews is provided, use those as presets
    if (configSavedViews && configSavedViews.length > 0) {
      return configSavedViews.map((view) => ({
        label: view.name,
        filters: view.filters,
      }));
    }

    // Otherwise, use the hardcoded presets as fallback
    const presets: Array<{ label: string; filters: Record<string, FieldValue> }> = [];

    // Time Entries specific presets
    const hasDateWorked = config.fields.date_worked?.datetimePresets?.enabled;
    if (hasDateWorked) {
      presets.push(
        { label: "Today", filters: { date_worked: "today" } },
        { label: "This Week", filters: { date_worked: "this_week" } },
        { label: "This Month", filters: { date_worked: "this_month" } }
      );
    }

    if (config.fields.is_approved) {
      presets.push({ label: "Unapproved", filters: { is_approved: false } });
    }

    if (config.fields.entry_type) {
      presets.push({ label: "Billable Only", filters: { entry_type: "billable" } });
    }

    // Tickets specific presets
    if (config.fields.status && config.entity === "tickets") {
      presets.push(
        { label: "Open Tickets", filters: { status: "new" } },
        { label: "In Progress", filters: { status: "in_progress" } },
        { label: "Waiting", filters: { status: "waiting_for_customer" } }
      );
    }

    if (config.fields.priority && config.entity === "tickets") {
      presets.push(
        { label: "High Priority", filters: { priority: "high" } },
        { label: "Urgent", filters: { priority: "urgent" } }
      );
    }

    if (config.fields.has_time_entries && config.entity === "tickets") {
      presets.push({ label: "No Time Logged", filters: { has_time_entries: false } });
    }

    return presets;
  }, [config, configSavedViews]);
};

/**
 * Hook to encapsulate filter-related actions and callbacks
 */
export const useFilterActions = <TData extends BaseEntity>() => {
  const filterContext = useFilterContext<TData>();
  const { filters, actions } = filterContext;

  // Clear all filters and search
  const handleClearAll = useCallback(() => {
    actions.setSearchTerm("");
    actions.clearAll();
    actions.setShowAdvancedFilters(false);
  }, [actions]);

  // Remove a specific filter
  const handleRemoveFilter = useCallback(
    (fieldId: string) => {
      const newFilters = { ...filters.simple };
      delete newFilters[fieldId];
      // Update all simple filters at once
      Object.keys(newFilters).forEach((key) => {
        actions.setSimpleFilter(key, newFilters[key]);
      });
      // Remove the deleted filter
      actions.setSimpleFilter(fieldId, undefined);
    },
    [filters.simple, actions]
  );

  return {
    handleClearAll,
    handleRemoveFilter,
  };
};
